import PropTypes from 'prop-types';
import { Link } from 'react-router';

import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import SalesOrderStatusBadge from '#/modules/crmMgmt/salesOrder/components/SalesOrderStatusBadge';
import {
  displayOrDash,
  mapSalesOrderMeta,
} from '#/modules/crmMgmt/salesOrder/helper/mapSalesOrderMeta';
import { getSalesOrderStockIssueDetailPath } from '#/modules/crmMgmt/salesOrder/helper/salesOrderStatus';

function SalesOrderHero({ meta, actions, compact = false }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="flex min-w-0 flex-1 flex-col gap-5">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1
              className={cn(
                'font-bold text-gray-900',
                compact ? 'text-xl' : 'text-2xl',
              )}
            >
              {meta.code}
            </h1>
            {meta.status ? <SalesOrderStatusBadge status={meta.status} /> : null}
          </div>
          <p className="mt-1 text-sm text-gray-600">
            客戶：
            {meta.customerSubtitle}
          </p>
        </div>
      </div>
      {actions ? (
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          {actions}
        </div>
      ) : null}
    </div>
  );
}

SalesOrderHero.propTypes = {
  meta: PropTypes.shape({
    code: PropTypes.string,
    status: PropTypes.string,
    customerSubtitle: PropTypes.string,
  }).isRequired,
  actions: PropTypes.node,
  compact: PropTypes.bool,
};

function HistoryField({ label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">{value}</p>
    </div>
  );
}

HistoryField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
};

function renderStockIssueValue(stockIssueId) {
  if (!stockIssueId) {
    return null;
  }
  const stockIssuePath = getSalesOrderStockIssueDetailPath(stockIssueId);
  if (!stockIssuePath) {
    return stockIssueId;
  }
  return (
    <Link
      className="text-primary-600 underline-offset-2 hover:underline"
      to={stockIssuePath}
    >
      {stockIssueId}
    </Link>
  );
}

function SalesOrderInfoGrid({ meta, compact = false }) {
  const customerOrderNo = meta.customerOrderNo;
  const stockIssueValue = renderStockIssueValue(meta.stockIssueId);

  return (
    <div
      className={cn(
        'mt-4 grid grid-cols-1 gap-x-4 gap-y-3 border-t border-slate-200 pt-4',
        !compact && 'sm:grid-cols-2 md:grid-cols-4',
      )}
    >
      {/* 第一列：時間＋關聯單據（對齊客訂 info card：建立／修改在前） */}
      <HistoryField label="建立" value={meta.createDate} />
      <HistoryField label="修改" value={meta.reviseDate} />
      <HistoryField
        label="來源客戶訂單"
        value={meta.customerOrderId ? (
          <Link
            className="text-primary-600 underline-offset-2 hover:underline"
            to={ROUTES.customerOrderDetail(meta.customerOrderId)}
          >
            {customerOrderNo}
          </Link>
        ) : displayOrDash(null)}
      />
      <HistoryField label="出庫單" value={stockIssueValue ?? displayOrDash(null)} />

      {/* 第二列：交期與計價 */}
      <HistoryField label="預交日" value={meta.plannedShipDate} />
      <HistoryField label="幣別" value={meta.currencyCode} />
      <HistoryField label="計稅模式" value={meta.priceTaxMode} />
      <div className={cn('hidden', !compact && 'md:block')} aria-hidden="true" />

      {/* 第三列：事由／備註 */}
      <div className={cn(!compact && 'sm:col-span-2 md:col-span-2')}>
        <p className="text-xs text-gray-500">事由</p>
        <p className="mt-0.5 text-sm font-medium wrap-break-word text-gray-900">
          {displayOrDash(meta.issueReason)}
        </p>
      </div>
      <div className={cn(!compact && 'sm:col-span-2 md:col-span-2')}>
        <p className="text-xs text-gray-500">備註</p>
        <p className="mt-0.5 text-sm font-medium wrap-break-word text-gray-900">
          {displayOrDash(meta.remark)}
        </p>
      </div>
    </div>
  );
}

SalesOrderInfoGrid.propTypes = {
  meta: PropTypes.object.isRequired,
  compact: PropTypes.bool,
};

const SalesOrderMainInfoCard = ({
  detail,
  embedded = false,
  nested = false,
  compact = false,
  actions = null,
  containerClassName = '',
}) => {
  const meta = mapSalesOrderMeta(detail);
  const surfacePaddingClassName = compact ? 'p-4' : 'p-5';

  const content = (
    <>
      <SalesOrderHero meta={meta} actions={actions} compact={compact} />
      <SalesOrderInfoGrid meta={meta} compact={compact} />
    </>
  );

  if (nested) {
    return content;
  }

  if (embedded) {
    return (
      <div className={cn('border-b border-slate-200', surfacePaddingClassName)}>
        {content}
      </div>
    );
  }

  return (
    <div className={cn('receipt-card-elevate', containerClassName)}>
      <div className={cn('receipt-card-surface', surfacePaddingClassName)}>
        {content}
      </div>
    </div>
  );
};

SalesOrderMainInfoCard.propTypes = {
  detail: PropTypes.object.isRequired,
  embedded: PropTypes.bool,
  nested: PropTypes.bool,
  compact: PropTypes.bool,
  actions: PropTypes.node,
  containerClassName: PropTypes.string,
};

export default SalesOrderMainInfoCard;
